import React, { useEffect, useState } from 'react';
import authFetch from '../api';
import { ClipboardCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function AdminAssignEmployees() {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Check if user is logged in
        const token = localStorage.getItem("jwtToken");
        if (!token) {
          console.log("No JWT token found, redirecting to login");
          window.location.href = "/login";
          return;
        }
        
        console.log('🔍 Fetching projects and employees...');
        
        const [projRes, empRes] = await Promise.all([
          authFetch('/projects/assigned'),
          authFetch('/users')
        ]);
        
        // Handle projects response
        let projectsData;
        if (projRes && projRes.data) {
          projectsData = projRes.data;
        } else {
          projectsData = projRes;
        }
        console.log('📊 Projects data received:', projectsData);
        setProjects(Array.isArray(projectsData) ? projectsData : []);
        
        // Handle employees response
        let usersData;
        if (empRes && empRes.data) {
          usersData = empRes.data;
        } else {
          usersData = empRes;
        }
        console.log('📊 Users data received:', usersData);
        
        // Ensure employees is always an array and filter for employees
        const allUsers = usersData.users || usersData || [];
        const employeeUsers = Array.isArray(allUsers) ? allUsers.filter(u => 
          u.role === 'Employee' || u.role === 'employee' || u.role === 'Служител'
        ) : [];
        console.log('👥 Employee users filtered:', employeeUsers);
        setEmployees(employeeUsers);
        
      } catch (err) {
        console.error('❌ Error loading data:', err);
        console.error('Error details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleAssign = async () => {
    if (!selectedProject || !selectedEmployee) {
      alert(t('adminAssignEmployees.selectBothProjectAndEmployee'));
      return;
    }
    
    try {
      console.log('🔍 Assigning employee to project:', {
        projectId: selectedProject,
        employeeId: selectedEmployee
      });
      
      await authFetch.patch(`/projects/${selectedProject}/assign/${selectedEmployee}`);
      
      console.log('✅ Assignment successful');
      alert(t('adminAssignEmployees.projectAssignedSuccessfully'));
      
      // Refresh the assignments data
      const res = await authFetch('/projects/assigned');
      let projectsData;
      if (res && res.data) {
        projectsData = res.data;
      } else {
        projectsData = res;
      }
      
      const projectsArray = Array.isArray(projectsData) ? projectsData : [];
      const data = projectsArray.map(proj => ({
        name: proj.name,
        employees: proj.assignedEmployees || []
      }));
      setAssignments(data);
      
      // Reset selections
      setSelectedProject('');
      setSelectedEmployee('');
      
    } catch (err) {
      console.error('❌ Assignment failed:', err);
      console.error('Error details:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      alert(t('adminAssignEmployees.assignmentFailed'));
    }
  };

  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        console.log('🔍 Fetching assignments...');
        const res = await authFetch('/projects/assigned');
        
        let projectsData;
        if (res && res.data) {
          projectsData = res.data;
        } else {
          projectsData = res;
        }
        
        console.log('📊 Assignments data received:', projectsData);
        
        // Ensure we have an array of projects
        const projectsArray = Array.isArray(projectsData) ? projectsData : [];
        
        const data = projectsArray.map(proj => ({
          name: proj.name,
          employees: proj.assignedEmployees || []
        }));
        
        console.log('📋 Processed assignments:', data);
        setAssignments(data);
      } catch (err) {
        console.error('❌ Error fetching assignments:', err);
        console.error('Error details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });
      }
    };
    fetchAssignments();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center pt-16">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('adminAssignEmployees.assignProjectToEmployee')}</h1>
            <p className="text-gray-600 mt-2">{t('adminAssignEmployees.manageProjectAssignments')}</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('adminAssignEmployees.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center pt-16">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('adminAssignEmployees.assignProjectToEmployee')}</h1>
            <p className="text-gray-600 mt-2">{t('adminAssignEmployees.manageProjectAssignments')}</p>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="text-center py-8">
            <div className="text-red-600 text-lg mb-4">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              {t('adminAssignEmployees.tryAgain')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Debug information
  console.log('🔍 AdminAssignEmployees Debug:', {
    projectsCount: projects.length,
    employeesCount: employees.length,
    assignmentsCount: assignments.length,
    selectedProject,
    selectedEmployee
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center pt-16">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('adminAssignEmployees.assignProjectToEmployee')}</h1>
          <p className="text-gray-600 mt-2">{t('adminAssignEmployees.manageProjectAssignments')}</p>
        </div>
      </div>

      {/* Assignment Form */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <h2 className="text-lg font-medium text-gray-900 mb-4">{t('adminAssignEmployees.projectAssignment')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminAssignEmployees.selectProject')}</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('adminAssignEmployees.chooseProject')}</option>
              {Array.isArray(projects) && projects.map((proj) => (
                <option key={proj._id} value={proj._id}>{proj.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('adminAssignEmployees.selectEmployee')}</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('adminAssignEmployees.chooseEmployee')}</option>
              {Array.isArray(employees) && employees.map((emp) => (
                <option key={emp._id} value={emp._id}>{emp.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleAssign}
            disabled={!selectedProject || !selectedEmployee}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-semibold transition-colors"
          >
            {t('adminAssignEmployees.assignProject')}
          </button>
        </div>
      </div>

      {/* Current Assignments */}
      <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
        <div className="p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('adminAssignEmployees.currentAssignments')}</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('adminAssignEmployees.project')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('adminAssignEmployees.assignedTo')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(assignments) && assignments.length > 0 ? (
                  assignments.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {Array.isArray(item.employees) && item.employees.length > 0
                          ? item.employees.map(emp => {
                              // Handle both object format (with name) and string format (just ID)
                              if (typeof emp === 'object' && emp.name) {
                                return emp.name;
                              } else if (typeof emp === 'string') {
                                // If it's just an ID, we might need to look it up
                                return `${t('adminAssignEmployees.employeeId')}: ${emp}`;
                              } else {
                                return t('adminAssignEmployees.unknown');
                              }
                            }).join(', ')
                          : t('adminAssignEmployees.noOneAssigned')}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2" className="px-6 py-12 text-center">
                      <ClipboardCheck className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-sm font-medium text-gray-900 mb-2">{t('adminAssignEmployees.noAssignmentsFound')}</h3>
                      <p className="text-sm text-gray-500">
                        {t('adminAssignEmployees.getStartedMessage')}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}